import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs font-bold text-gray-600" }, "Nova especialidade da empresa", -1)
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = ["data-testid"]
const _hoisted_6 = {
  key: 1,
  class: "mt-2 text-sm text-green-700"
}
const _hoisted_7 = { class: "mt-3 flex flex-wrap gap-2" }
const _hoisted_8 = ["data-testid"]
const _hoisted_9 = ["disabled", "data-testid"]
const _hoisted_10 = ["data-testid"]
const _hoisted_11 = ["data-testid"]

import { computed, ref, watch } from 'vue'
import CatalogCombobox from './CatalogCombobox.vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'
import { useSpecialtyStore } from '@/pinia-store/useSpecialtyStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import {
  findSpecialtyMatch,
  matchesCatalogSearch,
  type SpecialtySelection
} from '@/utils/sectorSpecialty'


export default /*#__PURE__*/_defineComponent({
  __name: 'SpecialtyCombobox',
  props: {
    modelValue: {},
    companyId: {},
    officialOnly: { type: Boolean, default: false },
    emptyText: { default: '— sem especialidade' },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    testId: { default: 'specialty-combobox' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const emitSelection = (value: SpecialtySelection | null): void => emit('update:modelValue', value)

const specialtyStore = useSpecialtyStore()
// Celular (QA-01): o reset do Vuetify zera `border-style` em <input>; o desktop fica como está.
const { isMobileLayout } = useIsMobile()
const search = ref('')
const isOtherMode = ref(false)
const otherName = ref('')

watch(
  () => props.companyId,
  (companyId) => {
    if (companyId) void specialtyStore.fetchOptions(companyId)
  },
  { immediate: true }
)

const options = computed(() => specialtyStore.optionsOf(props.companyId))
const loadError = computed(() =>
  props.companyId ? (specialtyStore.loadErrorByCompany[props.companyId] ?? null) : null
)
const displayValue = computed(() => props.modelValue?.name ?? '')
const displayBadge = computed(() => {
  if (!props.modelValue) return null
  if (props.modelValue.kind === 'official') return 'oficial'
  return props.modelValue.kind === 'company' ? 'da empresa' : 'nova'
})

const sections = computed<CatalogSection[]>(() => {
  const result: CatalogSection[] = []
  if (props.modelValue && props.clearable) {
    result.push({
      key: 'clear',
      title: null,
      options: [
        {
          key: 'clear',
          label: '— Sem especialidade',
          kind: 'clear',
          testId: `${props.testId}-clear-option`
        }
      ]
    })
  }
  const official = (options.value?.official ?? []).filter((o) =>
    matchesCatalogSearch(o.name, search.value)
  )
  result.push({
    key: 'official',
    title: 'Oficiais (CFM)',
    options: official.map((o) => ({
      key: `o:${o.id}`,
      label: o.name,
      kind: 'option',
      testId: `${props.testId}-option-o-${o.id}`
    }))
  })
  if (props.officialOnly) return result
  const company = (options.value?.company ?? []).filter((o) =>
    matchesCatalogSearch(o.name, search.value)
  )
  result.push({
    key: 'company',
    title: 'Da sua empresa',
    options: company.map((o) => ({
      key: `c:${o.id}`,
      label: o.name,
      kind: 'option',
      testId: `${props.testId}-option-c-${o.id}`
    }))
  })
  const typed = search.value.trim()
  result.push({
    key: 'other',
    title: null,
    options: [
      {
        key: 'other',
        label: typed ? `✎ Outra… usar “${typed}” como nova` : '✎ Outra… (digitar)',
        kind: 'action',
        testId: `${props.testId}-other-option`
      }
    ]
  })
  return result
})

const otherMatch = computed(() => findSpecialtyMatch(otherName.value, options.value))
const otherMatchText = computed(() => {
  if (!otherMatch.value) return ''
  const name = otherMatch.value.option.name
  return otherMatch.value.kind === 'official'
    ? `“${name}” já existe na lista oficial. Usar essa?`
    : `Já existe “${name}” na sua empresa. Usar essa?`
})

async function onOpen(): Promise<void> {
  if (props.companyId) await specialtyStore.fetchOptions(props.companyId)
}

function onSelect(option: CatalogOption): void {
  if (option.key === 'clear') {
    emitSelection(null)
    return
  }
  if (option.key === 'other') {
    otherName.value = search.value.trim()
    isOtherMode.value = true
    return
  }
  const [kind, rawId] = option.key.split(':')
  const id = Number(rawId)
  const list = kind === 'o' ? options.value?.official : options.value?.company
  const found = list?.find((o) => o.id === id)
  if (found)
    emitSelection({
      kind: kind === 'o' ? 'official' : 'company',
      id: found.id,
      name: found.name
    })
}

function useMatch(): void {
  if (!otherMatch.value) return
  const { kind, option } = otherMatch.value
  emitSelection({ kind, id: option.id, name: option.name })
  leaveOther()
}

function createNew(): void {
  const name = otherName.value.trim()
  if (!name) return
  emitSelection({ kind: 'new', name })
  leaveOther()
}

function leaveOther(): void {
  isOtherMode.value = false
  otherName.value = ''
  search.value = ''
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!isOtherMode.value)
      ? (_openBlock(), _createBlock(CatalogCombobox, {
          key: 0,
          label: "Especialidade",
          "display-value": displayValue.value,
          "display-badge": displayBadge.value,
          "empty-text": _ctx.emptyText,
          placeholder: "Buscar especialidade",
          sections: sections.value,
          search: search.value,
          disabled: _ctx.disabled || !_ctx.companyId,
          clearable: _ctx.clearable,
          "no-results-text": 
        search.value.trim()
          ? `Nenhuma especialidade encontrada para “${search.value.trim()}”.`
          : 'Nenhuma especialidade disponível.'
      ,
          "test-id": _ctx.testId,
          "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => (search.value = $event)),
          onSelect: onSelect,
          onClear: _cache[1] || (_cache[1] = ($event: any) => (emitSelection(null))),
          onOpen: onOpen
        }, null, 8, ["display-value", "display-badge", "empty-text", "sections", "search", "disabled", "clearable", "no-results-text", "test-id"]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "rounded-md border border-primary20 p-3 text-left",
          "data-testid": `${_ctx.testId}-other-panel`
        }, [
          _hoisted_3,
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((otherName).value = $event)),
            class: _normalizeClass(["mt-1 h-[40px] w-full rounded border border-gray-300 px-3 text-sm", { 'border-solid': _unref(isMobileLayout) }]),
            placeholder: "Nome da especialidade",
            maxlength: "120",
            "data-testid": `${_ctx.testId}-other-input`
          }, null, 10, _hoisted_4), [
            [_vModelText, otherName.value]
          ]),
          (otherMatch.value)
            ? (_openBlock(), _createElementBlock("p", {
                key: 0,
                class: "mt-2 text-sm text-gray-700",
                "data-testid": `${_ctx.testId}-other-match`
              }, _toDisplayString(otherMatchText.value), 9, _hoisted_5))
            : (otherName.value.trim())
              ? (_openBlock(), _createElementBlock("p", _hoisted_6, " ✓ Nenhuma especialidade parecida. Ao confirmar, “" + _toDisplayString(otherName.value.trim()) + "” vira especialidade da empresa. ", 1))
              : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_7, [
            (otherMatch.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "rounded bg-primary30 px-3 py-1.5 text-sm font-semibold text-white",
                  "data-testid": `${_ctx.testId}-use-existing-button`,
                  onClick: useMatch
                }, " Usar “" + _toDisplayString(otherMatch.value.option.name) + "”" + _toDisplayString(otherMatch.value.kind === 'official' ? ' (oficial)' : ''), 9, _hoisted_8))
              : (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "rounded bg-primary30 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50",
                  disabled: !otherName.value.trim(),
                  "data-testid": `${_ctx.testId}-create-button`,
                  onClick: createNew
                }, " Criar e usar ", 8, _hoisted_9)),
            _createElementVNode("button", {
              type: "button",
              class: "px-3 py-1.5 text-sm text-primary30",
              "data-testid": `${_ctx.testId}-back-button`,
              onClick: leaveOther
            }, " Voltar à busca ", 8, _hoisted_10)
          ])
        ], 8, _hoisted_2)),
    (loadError.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 2,
          class: "mt-1 text-xs text-red-600",
          "data-testid": `${_ctx.testId}-load-error`
        }, _toDisplayString(loadError.value), 9, _hoisted_11))
      : _createCommentVNode("", true)
  ]))
}
}

})